import { AppKeys } from "../Components/Shared/AppKeys";
/**
 * Helper utilities for printing functionality in the application
 */

/**
 * Creates and opens a print window for an exam
 * @param {Object} exam - The exam data object containing all exam info
 * @param {Array} questions - Array of questions for the exam
 */
export const printExam = (exam, questions) => {
  // Create a new window with a specific name to avoid about:blank
  const printWindow = window.open(
    "",
    `ExamPrint_${exam.id || new Date().getTime()}`,
    "width=800,height=600"
  );
  // Determine text direction and language based on material name
  const isRtl =
    exam.material_name !== AppKeys.materials._English &&
    exam.material_name !== AppKeys.materials._French;
  const htmlDirection = isRtl ? "rtl" : "ltr";
  const materialLanguage =
    exam.material_name === AppKeys.materials._English ? "en" : "ar";
  const materialName =
    exam.material_name === AppKeys.materials._English
      ? "English"
      : exam.material_name;

  // Parse options from JSON strings if needed
  const processedQuestions = questions.map((q) => {
    let options = q.options;
    // Check if options is a string and try to parse it
    if (typeof options === "string") {
      try {
        // First try to parse as JSON
        options = JSON.parse(options);
      } catch (e) {
        options = options.split(",").map((opt) => opt.trim());
      }
    }

    // If options is still a string after attempts to parse, or is null/undefined
    // set it as an empty array
    if (!Array.isArray(options)) {
      options = [];
    }

    return {
      ...q,
      question_text: q.question || "",
      options: Array.isArray(options)
        ? options.map((opt) => ({
          option_text: opt,
        }))
        : [],
    };
  });



  // Style for the print window
  const styles = `
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');

          @media print {
              @page {
                  size: A4;
                  margin: 1cm;
              }

              body {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
              }

              .no-print-break {
                  page-break-inside: avoid;
              }

              .page-break {
                  page-break-before: always;
              }

              /* Add page break after every 4th question */
              .question:nth-of-type(4n) {
                  page-break-before: always;
              }

              /* Watermark style for printing */
              body {
                  background-repeat: no-repeat;
                  background-position: center center; /* Center the watermark */
                  background-size: 500px; /* Adjust size: 'contain' or 'cover' */
                  background-attachment: fixed; /* Keep watermark fixed when scrolling */
                  z-index: 99999;
              }
          }

          * {
              box-sizing: border-box;
          }

          body {
              font-family: 'Cairo', Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              direction: ${htmlDirection};
              padding: 0;
              margin: 0;
              background-color:white;
          }

          .print-container {
              max-width: 210mm;
              margin: 0 auto;
              background:rgba(255, 255, 255, 0.76);
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
              padding: 20px;
              direction: ${htmlDirection};
          }

          .exam-header {
              text-align: center;
              padding-bottom: 20px;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              display: flex;
              position: relative;
          }

          .logo-container {
              position: absolute;
              right: 0;
              top: 0;
              width: 80px;
              height: 80px;
              display: flex;
              align-items: center;
              justify-content: center;
          }

          .logo {
              width: 80px;
              height: 80px;
              object-fit: contain;
          }

          .header-content {
              flex: 1;
              text-align: center;
          }

          .school-name {
              font-size: 18px;
              margin-bottom: 5px;
          }

          .exam-title {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
              color: #2c3e50;
          }

          .exam-subtitle {
              font-size: 18px;
              color: #7f8c8d;
              margin-bottom: 5px;
          }

          .exam-meta {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
              margin: 20px 0;
              background-color: #f8f9fa;
              padding: 15px;
              border-radius: 8px;
              border: 1px solid #e9ecef;
          }

          .exam-meta-item {
              margin-bottom: 10px;
              display: flex;
              align-items: center;
          }

          .exam-meta-item strong {
              margin-left: 5px;
              color: #2c3e50;
          }

          .exam-instructions {
              margin-bottom: 30px;
              padding: 15px;
              background-color: #e9f7fe;
              border: 1px solid #bee3f8;
              border-radius: 8px;
              position: relative;
          }

          .instructions-title {
              position: absolute;
              top: -12px;
              right: 20px;
              background-color: #3498db;
              color: white;
              padding: 2px 15px;
              border-radius: 20px;
              font-size: 14px;
              font-weight: bold;
          }

          .questions-container {
              counter-reset: question-counter;
          }

          .question {
              margin-bottom: 25px;
              padding: 15px;
              background-color: #fff;
              border: 1px solid #e9ecef;
              border-radius: 8px;
              box-shadow: 0 2px 5px rgba(0,0,0,0.05);
              position: relative;
          }

          .question-number {
              font-weight: bold;
              font-size: 18px;
              color: #3498db;
              display: inline-block;
              margin-bottom: 8px;
          }

          .question-text {
              margin-bottom: 15px;
              font-weight: 600;
              font-size: 16px;
              color: #2c3e50;
          }

          .options {
              margin-right: 20px;
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
          }

          .option {
              margin-bottom: 8px;
              padding: 8px 12px;
              background-color: #e4e7e9;
              border: 1px solid #e9ecef;
              border-radius: 6px;
              transition: all 0.2s;
          }

          .option:hover {
              background-color: #e9ecef;
          }

          .option-letter {
              display: inline-block;
              width: 24px;
              height: 24px;
              background-color: #3498db;
              color: white;
              border-radius: 50%;
              text-align: center;
              line-height: 24px;
              margin-left: 8px;
              font-weight: bold;
              font-size: 14px;
          }

          .exam-footer {
              margin-top: 30px;
              text-align: center;
              font-size: 12px;
              color: #7f8c8d;
              border-top: 1px solid #e9ecef;
              padding-top: 15px;
          }

          .print-button {
              display: block;
              margin: 20px auto;
              padding: 10px 20px;
              background-color: #3498db;
              color: white;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              font-family: 'Cairo', Arial, sans-serif;
              font-weight: bold;
              transition: all 0.3s;
          }

          .print-button:hover {
              background-color: #2980b9;
              transform: translateY(-2px);
              box-shadow: 0 5px 10px rgba(0,0,0,0.1);
          }

          .answer-sheet {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px dashed #ccc;
          }

          .answer-sheet-title {
              text-align: center;
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 20px;
              color: #2c3e50;
              position: relative;
          }

          .answer-sheet-title::before,
          .answer-sheet-title::after {
              content: "✂";
              position: absolute;
              top: -20px;
              color: #7f8c8d;
          }

          .answer-sheet-title::before {
              right: 20px;
          }

          .answer-sheet-title::after {
              left: 20px;
          }

          .student-info {
              display: flex;
              margin-bottom: 20px;
              gap: 20px;
          }

          .info-field {
              flex: 1;
              display: flex;
              flex-direction: column;
          }

          .info-field label {
              font-size: 14px;
              margin-bottom: 5px;
              color: #7f8c8d;
          }

          .info-field .input-line {
              height: 30px;
              border-bottom: 1px solid #ccc;
          }

          .answer-grid {
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              gap: 10px;
              margin-bottom: 30px;
          }

          .answer-item {
              padding: 10px;
              border: 1px solid #e9ecef;
              border-radius: 8px;
              text-align: center;
          }

          .answer-item .question-number {
              font-weight: bold;
              margin-bottom: 5px;
              color: #2c3e50;
          }

          .answer-options {
              display: flex;
              justify-content: space-between;
              padding: 0 10px;
          }

          .answer-option {
              width: 20px;
              height: 20px;
              border: 1px solid #ccc;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
          }

          @media print {
              .print-button {
                  display: none;
              }
          }

          /* For screens smaller than 600px */
          @media (max-width: 600px) {
              .options {
                  grid-template-columns: 1fr;
              }

              .answer-grid {
                  grid-template-columns: repeat(3, 1fr);
              }

              .exam-meta {
                  grid-template-columns: 1fr;
              }
          }
      </style>
    `;

  // Helper function to generate option letters (أ, ب, ج, د)
  const getOptionLetter = (index, lang = "ar") => {
    let res =
      lang === "ar"
        ? ["أ", "ب", "ج", "د"][index] || index.toString()
        : ["A", "B", "C", "D"][index] || index.toString();
    return res;
  };

  // Format exam duration
  const formatDuration = (duration, lang) => {
    if (!duration) return "";
    const [hours, minutes] = duration.split(":");
    return `${hours > 0 ? hours : ''} ${hours > 0 ? (lang === 'ar' ? 'ساعة و ' : 'and hour') : ''} ${minutes} ${lang === 'ar' ? 'دقيقة و ' : 'and Min'}`;
  };

  // Format exam date
  const formatExamDate = (date, lang = 'ar') => {
    if (!date) return '';
    if (lang === 'ar') return new Date(date).toLocaleDateString("ar-EG");
    else return new Date(date).toLocaleDateString();
  };

  // Generate questions HTML
  const questionsHTML = processedQuestions
    .map((question, index) => {
      const options = question.options || [];

      return `
            <div class="question no-print-break">
                <div class="question-text"><span class="question-number">${index + 1
        } - </span> ${question.question_text}</div>
                <div class="options">
                    ${options
          .map(
            (option, optIndex) => `
                        <div class="option">
                            <span class="option-letter">${getOptionLetter(
              optIndex,
              materialLanguage
            )}</span>
                            ${option.option_text}
                        </div>
                    `
          )
          .join("")}
                </div>
            </div>
        `;
    })
    .join("");
  // Generate answer sheet HTML
  const answerSheetHTML = `
        <div class="answer-sheet page-break">
            <div class="answer-sheet-title">${materialLanguage === "ar" ? "ورقة الإجابة" : "Answer Sheet"}</div>
            
            <div class="student-info">
                <div class="info-field">
                    <label>${materialLanguage === "ar" ? "اسم الطالب:" : "Student Name:"}</label>
                    <div class="input-line"></div>
                </div>
                <div class="info-field">
                    <label>${materialLanguage === "ar" ? "الرقم:" : "ID Number:"}</label>
                    <div class="input-line"></div>
                </div>
                <div class="info-field">
                    <label>${materialLanguage === "ar" ? "التاريخ:" : "Date:"}</label>
                    <div class="input-line"></div>
                </div>
            </div>
            
            <div class="answer-grid">
                ${processedQuestions
      .map(
        (_, index) => `
                    <div class="answer-item">
                        <div class="question-number">${index + 1}</div>
                        <div class="answer-options">
                            <div class="answer-option">${getOptionLetter(0, materialLanguage)}</div>
                            <div class="answer-option">${getOptionLetter(1, materialLanguage)}</div>
                            <div class="answer-option">${getOptionLetter(2, materialLanguage)}</div>
                            <div class="answer-option">${getOptionLetter(3, materialLanguage)}</div>
                        </div>
                    </div>
                `
      )
      .join("")}
            </div>
            
            <div class="exam-footer">
                <p>${materialLanguage === "ar" ? "جميع الحقوق محفوظة © نظام الاختبارات الإلكتروني - منصة EMt7any" : "All Rights Reserved © Electronic Examination System - EMt7any Platform"}</p>
            </div>
        </div>
    `;

  const htmlContent = `
    <!DOCTYPE html>
    <html dir="${htmlDirection}" lang="${materialLanguage}">
    <head>
        <meta charset="UTF-8">
        <title>${materialName}</title>
        ${styles}
  </head>
  <body>
        <div class="print-container">
            <div class="exam-header">
                <div class="logo-container">
                   <img width="150" height="150" src='https://emt7any.premiumasp.net/AppView/d65d7a95-1a88-4692-b813-0541a6359f08.png'>
                </div>
                 <div class="header-content">
                     <div class="exam-title">${exam.exam_title}</div>
                     <div class="exam-subtitle">${materialName || "لا يوجد مادة"
    } - ${exam.user_class_name || "لا يوجد صف"}</div>
                 </div>
             </div>

            <div class="exam-meta">
                <div class="exam-meta-item">
                    <strong>${materialLanguage === "ar" ? "تاريخ الاختبار" : "Exam Date"
    } : </strong>
                    <span>${formatExamDate(
      exam.exam_date,
      materialLanguage
    )}</span>
                </div>
                <div class="exam-meta-item">
                    <strong>${materialLanguage === "ar"
      ? "مدة الاختبار"
      : "Exam Duration"
    } : </strong>
                    <span>  ${formatDuration(exam.exam_duration, materialLanguage)}</span>
                </div>
                <div class="exam-meta-item">
                    <strong>${materialLanguage === "ar"
      ? "مجموع الدرجات"
      : "Total Scores"
    }  : </strong>
                    <span>  ${questions.length > 1 ? questions.length : "غير متاح"
    }</span>
                </div>
                <div class="exam-meta-item">
                    <strong>${materialLanguage === "ar"
      ? "اسئلة الاختبار"
      : "Exam Questions"
    } : </strong>
                    <span>  ${questions.length} ${questions.length > 1
      ? materialLanguage === "ar"
        ? "أسئلة الجمع"
        : "Questions Plural"
      : materialLanguage === "ar"
        ? "أسئلة المفرد"
        : "Questions Singular"
    }</span>
                </div>
            </div>

            ${exam.instructions
      ? `
            <div class="exam-instructions">
                <div class="instructions-title">${materialLanguage === "ar"
        ? "ارشادات الاختبار"
        : "Exam Instructions"
      }</div>
                <ol>
                    ${exam.instructions
        .split("\n")
        .map((instruction) => `<li>${instruction}</li>`)
        .join("")}
                </ol>
            </div>
            `
      : ""
    }

            <div class="questions-container">
                ${questionsHTML}
            </div>

            ${answerSheetHTML}
        </div>

        <button class="print-button" id="printButton" onclick="window.print();">${materialLanguage === "ar" ? "طباعة الاختبار" : "Exam Print"
    }</button>

        <script>
            // Set focus to this window
            window.focus();
            // Auto-print option - uncomment to enable automatic printing
            // setTimeout(function() { window.print(); }, 1000);
        </script>
  </body>
  </html>
`;

  // Write the HTML content to the print window
  printWindow.document.open();
  printWindow.document.write(htmlContent);
  printWindow.document.close();

  // Focus on the new window
  printWindow.focus();

  // Return the window reference in case further operations are needed
  return printWindow;
};

/**
 * Formats exam data and fetches questions before printing
 * @param {Object} exam - The exam data object
 * @param {Function} fetchQuestions - Function to fetch exam questions
 * @returns {Promise} - Promise resolving after print window is opened
 */
export const preparePrintExam = async (exam, fetchQuestions) => {
  try {
    // Fetch questions if they're not provided
    const response = await fetchQuestions(exam.id);

    // Get questions from the response
    let questions = [];

    if (response && response.data) {
      questions = response.data;
    } else if (Array.isArray(response)) {
      questions = response;
    }

    // If no questions, throw error
    if (!questions || questions.length === 0) {
      throw new Error("لا توجد أسئلة لهذا الامتحان");
    }

    // Print the exam with questions
    return printExam(exam, questions);
  } catch (error) {
    throw error;
  }
};
